import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { roleLabel } from '@/components/wom/theme';
import { authorizedWorkspaces, FUTURE_APPS } from '@/lib/workspaces';
import { TOTAL_E_SLOGAN } from '@/lib/brand';
import { Crown, LogOut, ChevronRight, CalendarClock, CloudSun, RotateCcw, Sun, Moon, Cloud, Cloudy, CloudRain, CloudLightning, CloudMoon } from 'lucide-react';
import { Avatar } from '@/components/admin/Avatar';
import { catOf } from '@/components/WelcomeCalendar';
import { getEntrance, postLogoutPath } from '@/lib/entrance';
import { NotificationBell } from '@/components/welcome/NotificationBell';
import { CalendarPreview } from '@/components/welcome/CalendarPreview';

const API = process.env.REACT_APP_BACKEND_URL;
const hexA = (hex, a) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

// Exact final icon assets — size-normalized so every tile's artwork fills an
// identical square footprint (uniform visible dimensions, no distortion).
const ICON = {
  tfs_admin: '/app-icons-norm/tfs_admin_norm.webp',
  tfs_ops: '/app-icons-norm/tfs_ops_norm.webp',
  rout_admin: '/app-icons-norm/rout_admin_norm.webp',
  tfs_crm: '/app-icons-norm/tfs_crm_norm.webp',
  contract_budgets: '/app-icons-norm/contract_budgets_norm.webp',
  payroll: '/app-icons-norm/payroll_norm.webp',
  team: '/app-icons-norm/team_norm.webp',
  partners: '/app-icons-norm/partners_norm.webp',
  calendar: '/app-icons-norm/calendar_norm.webp',
  rout_client: '/app-icons-norm/rout_client_norm.webp',
  field: '/app-icons-norm/field_norm.webp',
};
// Restrained, consistent hover highlights per approved spec.
const ACCENT = {
  tfs_admin: '#C0C5CE', tfs_ops: '#5FA718', rout_admin: '#EADFC6', tfs_crm: '#5FA718',
  contract_budgets: '#7C3AED', payroll: '#F97316', team: '#1E3A8A', partners: '#B91C1C',
  calendar: '#C0C5CE', rout_client: '#C0C5CE', field: '#C0C5CE',
};
// Functional presentation (title / brand tag / description) — destinations unchanged.
const META = {
  rout_admin: { title: 'Work Orders', brand: 'ROUT', desc: 'Requests & job completion' },
  tfs_crm: { title: 'CRM', brand: 'REACH', desc: 'Prospects & sales' },
  contract_budgets: { title: 'Budgets', brand: 'TALLY', desc: 'Costs & pricing' },
  payroll: { title: 'Payroll', brand: 'Total Pay', desc: 'Pay preparation' },
  team: { title: 'T.E.A.M.', brand: '', desc: 'People & timekeeping' },
  partners: { title: 'Partners', brand: '', desc: 'Partner collaboration' },
  calendar: { title: 'Calendar', brand: '', desc: 'Company events & obligations' },
  rout_client: { title: 'Client Portal', brand: '', desc: 'Requests & service visibility' },
  field: { title: 'TFS Field Portal', brand: '', desc: 'Assigned work & reporting' },
};

const iconOf = (w) => ICON[w.key] || w.img;
const accentOf = (w) => ACCENT[w.key] || w.accent || '#5FA718';
const isActive = (w) => w.status !== 'coming' && !w.placeholder;
// One uniform, large icon size used for EVERY tile on the hub.
const ICON_PX = 80;

const StatusRow = ({ w }) => {
  const active = isActive(w);
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: active ? '#8FC93A' : 'rgba(148,163,184,0.9)' }} data-testid={`status-dot-${w.key}`} />
      <span className="text-xs font-semibold text-slate-300" data-testid={`status-${w.key}`}>{active ? 'Active' : 'Coming'}</span>
    </div>
  );
};

// Left-aligned artwork so visible left edges line up across cards (all sources are square).
const CardIcon = ({ w, px = ICON_PX }) => (
  <span className="flex shrink-0 items-center" style={{ width: px, height: px }}>
    <img src={iconOf(w)} alt="" width={px} height={px} loading="eager" decoding="async"
      style={{ width: px, height: px, objectPosition: 'left center' }}
      className="object-contain drop-shadow transition-transform duration-200 group-hover:scale-105" />
  </span>
);

// ---- Governance / Administration (full-width, boxy, primary control) ----
const GovernanceCard = ({ w, onActivate, crown }) => {
  const [hot, setHot] = useState(false);
  const accent = accentOf(w);
  return (
    <button
      data-testid={`workspace-tile-${w.key}`}
      onClick={() => onActivate(w)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      style={{ borderColor: hot ? accent : 'rgba(255,255,255,0.12)', boxShadow: hot ? `0 0 0 3px ${hexA(accent, 0.28)}` : 'none' }}
      className="group relative flex w-full min-w-0 items-center gap-4 overflow-hidden rounded-2xl border bg-white/[0.05] px-4 py-3 text-left outline-none backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08] focus-visible:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
    >
      <CardIcon w={w} px={Math.round(ICON_PX * 1.22)} />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          {crown && <Crown size={16} className="shrink-0" style={{ color: '#F5B301' }} fill="#F5B301" />}
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{ backgroundColor: hexA('#5FA718', 0.95), color: '#0E2A47' }}>Primary Control</span>
        </div>
        <div className="text-lg font-extrabold leading-tight text-white sm:text-xl" data-testid="governance-title">TFS Administration Hub</div>
        <div className="mt-0.5 text-sm text-slate-300">Shared data &amp; system controls.</div>
      </div>
      <ChevronRight size={22} className="ml-2 shrink-0 self-center transition-transform group-hover:translate-x-0.5" style={{ color: accent }} />
    </button>
  );
};

// ---- Wide card (Operations) — icon left, larger, left-edge aligned ----
const WideCard = ({ w, onActivate, pill }) => {
  const [hot, setHot] = useState(false);
  const accent = accentOf(w);
  const active = isActive(w);
  return (
    <button
      data-testid={`workspace-tile-${w.key}`}
      onClick={() => onActivate(w)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      aria-disabled={!active}
      style={{ borderColor: hot ? accent : 'rgba(255,255,255,0.10)', boxShadow: hot ? `0 0 0 3px ${hexA(accent, 0.28)}` : 'none' }}
      className="group relative flex w-full min-w-0 items-center gap-4 overflow-hidden rounded-2xl border bg-white/[0.045] px-4 py-3 text-left outline-none backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08] focus-visible:-translate-y-0.5 active:scale-[0.98] active:translate-y-0 min-h-[86px]"
    >
      <CardIcon w={w} />
      <div className="min-w-0 flex-1">
        {pill && (
          <span className="mb-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{ backgroundColor: hexA('#5FA718', 0.85), color: '#0E2A47' }}>{pill}</span>
        )}
        <div className="truncate text-lg font-extrabold leading-tight text-white">TFS Operations Hub</div>
        <div className="mt-0.5 text-xs text-slate-300">Inspections • Corrective actions • Fleet • Field oversight</div>
        <StatusRow w={w} />
      </div>
      <ChevronRight size={22} className="shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: accent }} />
    </button>
  );
};

// ---- Standard app card (icon left; title + brand tag + description + status) ----
const AppCard = ({ w, onActivate }) => {
  const [hot, setHot] = useState(false);
  const accent = accentOf(w);
  const active = isActive(w);
  const m = META[w.key] || { title: w.label || w.title, brand: '', desc: w.subtitle || '' };
  return (
    <button
      data-testid={`workspace-tile-${w.key}`}
      onClick={() => onActivate(w)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      aria-disabled={!active}
      style={{ borderColor: hot ? accent : 'rgba(255,255,255,0.10)', boxShadow: hot ? `0 0 0 3px ${hexA(accent, 0.28)}` : 'none' }}
      className="group relative flex h-full w-full min-w-0 items-center gap-3 overflow-hidden rounded-2xl border bg-white/[0.045] px-4 py-3 text-left outline-none backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08] focus-visible:-translate-y-0.5 active:scale-[0.98] active:translate-y-0 min-h-[86px]"
    >
      <CardIcon w={w} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-extrabold leading-tight text-white">{m.title}</div>
        <div className="mt-0.5 text-xs leading-snug text-slate-300">{m.desc}</div>
        <StatusRow w={w} />
      </div>
    </button>
  );
};

const SectionHead = ({ children, caption, testid }) => (
  <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
    <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-300" data-testid={testid}>{children}</h2>
    {caption && <p className="text-xs text-slate-400/80">{caption}</p>}
  </div>
);
const SubHead = ({ children, testid }) => (
  <h3 className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500" data-testid={testid}>{children}</h3>
);

// ---- Weekly scrolling activity banner (Mon–Sun items the user is permitted to see) ----
const WeeklyBanner = ({ token, onOpen }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let on = true;
    const etStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const et = new Date(`${etStr}T00:00:00`);
    const monday = new Date(et); monday.setDate(et.getDate() - ((et.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString('en-CA');
    axios.get(`${API}/api/calendar/events`, {
      params: { start: fmt(monday), end: fmt(sunday) },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => { if (on) setItems(Array.isArray(r.data?.events) ? r.data.events : []); })
      .catch(() => { if (on) setItems([]); });
    return () => { on = false; };
  }, [token]);

  const empty = items !== null && items.length === 0;
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.05]" data-testid="weekly-banner">
      <div className="flex items-stretch">
        <div className="flex shrink-0 items-center gap-2 border-r border-white/10 bg-white/[0.04] px-4 text-xs font-bold uppercase tracking-wider text-[#8FC93A]">
          <CalendarClock size={15} /> This Week
        </div>
        <div className="marquee relative min-w-0 flex-1 overflow-hidden py-4" data-testid="weekly-banner-track">
          {items === null && <div className="px-4 text-sm text-slate-400">Loading this week’s agenda…</div>}
          {empty && <div className="px-4 text-sm text-slate-400" data-testid="weekly-banner-empty">No scheduled items due this week.</div>}
          {items && items.length > 0 && (
            <div className="marquee-inner flex w-max items-center gap-7 whitespace-nowrap pr-7">
              {[...items, ...items].map((it, i) => {
                const c = catOf(it.source);
                return (
                  <button key={i} onClick={() => onOpen(it)} data-testid={`weekly-item-${i}`}
                    className="group/it inline-flex items-center gap-2.5 rounded-full py-0.5 pl-1 pr-2 text-sm text-slate-200 outline-none transition-colors hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-white/40">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/20" style={{ backgroundColor: c.color }} />
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: c.color, color: c.text }}>{it.source_label || c.name}</span>
                    <span className="font-semibold">{it.title}</span>
                    {it.detail && <span className="text-xs text-slate-400">{it.detail}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Footer clock + live Miami weather (National Weather Service via backend;
//      cached 30 min server-side, graceful, never blocks the welcome screen) ----
const WX_CACHE_KEY = 'te_miami_wx_v2';

// Compact condition icon honoring day/night; neutral cloud for anything unmapped.
const WeatherGlyph = ({ category, isDay }) => {
  const day = isDay !== false; // default to day when unknown
  const common = { size: 20, 'aria-hidden': true };
  switch (category) {
    case 'clear':
      return day ? <Sun {...common} className="text-amber-300" /> : <Moon {...common} className="text-slate-200" />;
    case 'partly_cloudy':
      return day ? <CloudSun {...common} className="text-amber-300" /> : <CloudMoon {...common} className="text-slate-200" />;
    case 'overcast':
      return <Cloudy {...common} className="text-slate-300" />;
    case 'rain':
      return <CloudRain {...common} className="text-sky-300" />;
    case 'thunderstorm':
      return <CloudLightning {...common} className="text-yellow-300" />;
    default:
      return <Cloud {...common} className="text-slate-300" />;
  }
};

const FooterStatus = () => {
  const [now, setNow] = useState(new Date());
  const [wx, setWx] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WX_CACHE_KEY) || 'null'); } catch { return null; }
  });
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000 * 30); return () => clearInterval(t); }, []);
  useEffect(() => {
    let on = true;
    const fetchWx = async () => {
      try {
        const r = await axios.get(`${API}/api/weather/miami`, { timeout: 9000 });
        if (on && r.data) {
          setWx(r.data);
          try { localStorage.setItem(WX_CACHE_KEY, JSON.stringify(r.data)); } catch { /* ignore */ }
        }
      } catch { /* keep last cached reading; never blank the screen */ }
    };
    fetchWx();
    const refresh = setInterval(fetchWx, 1000 * 60 * 30); // refresh every 30 min
    return () => { on = false; clearInterval(refresh); };
  }, []);

  const et = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', ...opts }).format(now);
  const time = et({ hour: 'numeric', minute: '2-digit', hour12: true });
  const date = et({ year: '2-digit', month: '2-digit', day: '2-digit' });

  const available = !!wx?.available;
  const hasTemp = available && Number.isFinite(wx?.temp_f);
  const label = available ? wx?.condition_label : null;
  const hasPrecip = available && Number.isFinite(wx?.precip_next_hour_pct);
  const accessible = available
    ? `Miami weather: ${hasTemp ? `${wx.temp_f} degrees Fahrenheit, ` : ''}${label || 'current conditions'}${hasPrecip ? `, rain next hour ${wx.precip_next_hour_pct} percent` : ''}`
    : 'Miami weather unavailable';

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-center sm:justify-end sm:text-right" data-testid="footer-status">
      <div data-testid="footer-datetime">
        <div className="text-sm font-semibold text-white" data-testid="footer-time">{time} ET</div>
        <div className="text-xs text-slate-400" data-testid="footer-date">{date}</div>
      </div>
      <div className="hidden h-8 w-px bg-white/10 sm:block" />
      <div className="flex items-center gap-2" data-testid="footer-weather" role="group" aria-label={accessible}>
        <WeatherGlyph category={wx?.condition_category} isDay={wx?.is_day} />
        <div className="text-left">
          {available ? (
            <>
              <div className="text-sm font-semibold text-white" data-testid="footer-weather-temp">
                {hasTemp ? `${wx.temp_f}°F` : <span className="font-normal text-slate-400">Temp unavailable</span>}
                {label ? <span className="font-normal text-slate-300" data-testid="footer-weather-condition"> · {label}</span> : null}
              </div>
              <div className="text-xs text-slate-400">
                {hasPrecip && <span data-testid="footer-weather-rain">Rain next hour: {wx.precip_next_hour_pct}%</span>}
                {hasPrecip && <span className="px-1">·</span>}
                <span>Miami, FL</span>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-300" data-testid="footer-weather-unavailable">Weather unavailable</div>
              <div className="text-xs text-slate-400">Miami, FL</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Calendar card — a SINGLE card: icon + title + up to 3 upcoming events ----
const CalendarCard = ({ w, onActivate, token }) => {
  const [hot, setHot] = useState(false);
  const accent = accentOf(w);
  const m = META[w.key] || { title: w.title, desc: w.subtitle || '' };
  return (
    <div
      data-testid="workspace-tile-calendar"
      role="button" tabIndex={0}
      onClick={() => onActivate(w)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(w); } }}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      style={{ borderColor: hot ? accent : 'rgba(255,255,255,0.10)', boxShadow: hot ? `0 0 0 3px ${hexA(accent, 0.28)}` : 'none' }}
      className="group relative flex h-full w-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-2xl border bg-white/[0.045] px-4 py-3 outline-none backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08] focus-visible:-translate-y-0.5"
    >
      <div className="flex min-w-0 items-center gap-3">
        <CardIcon w={w} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-extrabold leading-tight text-white">{m.title}</div>
          <div className="mt-0.5 text-xs leading-snug text-slate-300">{m.desc}</div>
        </div>
      </div>
      <CalendarPreview token={token} />
    </div>
  );
};

function WorkspaceSelector() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const workspaces = authorizedWorkspaces(user);
  const anyAccess = workspaces.length > 0;
  const find = (k) => workspaces.find((w) => w.key === k);
  const futureApp = (k) => FUTURE_APPS.find((w) => w.key === k);

  const adminWs = find('tfs_admin');
  const opsWs = find('tfs_ops');
  // Resume: remember the user's most recently opened app for the header control.
  const [lastKey, setLastKey] = useState(null);
  useEffect(() => {
    if (!token) return;
    let on = true;
    axios.get(`${API}/api/me/app-usage/last`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (on) setLastKey(r.data?.last_key || null); })
      .catch(() => {});
    return () => { on = false; };
  }, [token]);
  // Resume only to a destination the user is STILL authorized to open (workspace or
  // an active company-wide app such as the Calendar).
  const resumeCandidate = lastKey
    ? (workspaces.find((w) => w.key === lastKey) || (anyAccess ? futureApp(lastKey) : null))
    : null;
  const resumeWs = resumeCandidate && isActive(resumeCandidate) ? resumeCandidate : null;
  // WORKSPACES grid: Work Orders, CRM, Budgets / Payroll, T.E.A.M., Partners.
  const appGrid = [
    find('rout_admin'), find('tfs_crm'), find('contract_budgets'),
    find('payroll'), find('team'), find('rout_client'),
  ].filter(Boolean);
  // CONNECT & ACCESS: Calendar + the ROUT Client Portal.
  const calendarApp = anyAccess ? futureApp('calendar') : null;
  const portals = [].filter(Boolean);

  useEffect(() => {
    if (workspaces.length === 1 && workspaces[0].route) navigate(workspaces[0].route, { replace: true });
  }, [workspaces, navigate]);

  const handleActivate = (w) => {
    if (!w) return;
    if (w.placeholder || w.status === 'coming') return;
    // Remember the most-used app (fire and forget) so the hub can surface it first.
    if (token && w.key) {
      axios.post(`${API}/api/me/app-usage`, { app_key: w.key }, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
    if (w.external) { window.open(w.external, '_blank', 'noopener,noreferrer'); return; }
    navigate(w.route);
  };
  const handleLogout = () => { logout(); window.location.assign(getEntrance() ? postLogoutPath() : '/login'); };
  const openAgendaItem = (it) => { if (it?.route) navigate(it.route); };
  const handleResume = () => {
    // Return to the last authorized destination; if none/unauthorized, stay on Welcome.
    if (resumeWs?.route) { handleActivate(resumeWs); return; }
  };
  const resumeLabel = resumeWs ? (META[resumeWs.key]?.title || resumeWs.label || resumeWs.title || 'Resume') : null;

  const hasWorkspaces = opsWs || appGrid.length > 0;
  const hasConnect = calendarApp || portals.length > 0;

  return (
    <div className="min-h-screen bg-[#0E2A47] text-white" data-testid="workspace-selector">
      <style>{`
        @keyframes teMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .marquee-inner { animation: teMarquee 78s linear infinite; }
        .marquee:hover .marquee-inner, .marquee:focus-within .marquee-inner { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .marquee-inner { animation: none; } .marquee { overflow-x: auto; } }
      `}</style>

      {/* HEADER — full-width near-black ecosystem bar */}
      <header className="w-full border-b border-white/10 bg-[#101418]" data-testid="welcome-header">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/total_e_icon.webp" alt="Total-E" className="h-[56px] w-[56px] shrink-0 rounded-2xl object-contain drop-shadow-lg sm:h-[64px] sm:w-[64px]" data-testid="total-e-icon" />
            <div>
              <h1 className="text-2xl font-extrabold leading-none tracking-tight text-white sm:text-3xl" data-testid="welcome-heading">
                Welcome, {user?.name ? user.name.split(' ')[0] : 'User'}
              </h1>
              <p className="mt-1 text-sm font-medium text-slate-200">{TOTAL_E_SLOGAN}</p>
              <p className="mt-0.5 text-sm text-slate-300">Signed in as <span className="font-semibold text-white">{roleLabel(user?.role)}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 self-start sm:gap-3 sm:self-center">
            {resumeWs && (
              <button onClick={handleResume} data-testid="header-resume-btn" title={`Resume ${resumeLabel}`}
                aria-label={`Resume ${resumeLabel}`}
                className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/5 p-1 outline-none transition-colors hover:bg-white/15 focus-visible:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/40">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10">
                  <RotateCcw size={15} className="text-[#8FC93A]" />
                </span>
                <img src={iconOf(resumeWs)} alt="" className="h-7 w-7 shrink-0 rounded-md object-contain" />
              </button>
            )}
            <NotificationBell token={token} />
            <div className="rounded-full ring-2 ring-white/25" title={user?.name} data-testid="selector-user-avatar">
              <Avatar userId={user?.id} hasPhoto={user?.has_photo ?? !!user?.photo_url} name={user?.name} size={48} />
            </div>
            <button onClick={handleLogout} data-testid="selector-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-3 py-2 text-sm font-semibold text-white outline-none transition-colors hover:bg-white/15 focus-visible:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/40 sm:px-4">
              <LogOut size={16} /> <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="pointer-events-none fixed inset-0" aria-hidden="true"
        style={{ background: 'radial-gradient(1200px 500px at 15% 6%, rgba(143,201,58,0.08), transparent 60%), radial-gradient(1000px 500px at 100% 0%, rgba(255,255,255,0.04), transparent 55%)' }} />

      <div className="relative mx-auto max-w-7xl px-5 pb-4 pt-3 sm:px-6">
        {!anyAccess ? (
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 px-8 py-16 text-center" data-testid="selector-empty">
            <p className="text-lg text-slate-200">No workspaces are assigned to your account.</p>
            <p className="mt-2 text-sm text-slate-400">Contact your administrator to be granted access.</p>
          </div>
        ) : (
          <>
            {/* THIS WEEK banner */}
            <WeeklyBanner token={token} onOpen={openAgendaItem} />

            {/* GOVERNANCE */}
            {adminWs && (
              <section className="mt-3" data-testid="section-governance">
                <SectionHead testid="head-governance">Governance</SectionHead>
                <GovernanceCard w={adminWs} onActivate={handleActivate} crown />
              </section>
            )}

            {/* WORKSPACES (left) + CONNECT & ACCESS (right) */}
            <div className="mt-4 grid gap-5 lg:grid-cols-3">
              {hasWorkspaces && (
                <section className="min-w-0 lg:col-span-2" data-testid="section-workspaces">
                  <SectionHead testid="head-workspaces">Workspaces</SectionHead>
                  {opsWs && <WideCard w={opsWs} onActivate={handleActivate} pill="Operations" />}
                  {appGrid.length > 0 && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {appGrid.map((w) => <AppCard key={w.key} w={w} onActivate={handleActivate} />)}
                    </div>
                  )}
                </section>
              )}

              {hasConnect && (
                <section className="flex min-w-0 flex-col lg:col-span-1" data-testid="section-connect">
                  <SectionHead testid="head-connect">Connect &amp; Access</SectionHead>
                  {calendarApp && (
                    <div className="mt-0 flex min-h-0 flex-1 flex-col">
                      <CalendarCard w={calendarApp} onActivate={handleActivate} token={token} />
                    </div>
                  )}
                  {portals.length > 0 && (
                    <div className="mt-5 lg:mt-auto lg:pt-5">
                      <SubHead testid="head-portals">Portals</SubHead>
                      <div className="grid gap-3">
                        {portals.map((w) => (
                          <PortalCard key={w.key} w={w} onActivate={handleActivate} />
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>
          </>
        )}

        {/* FOOTER */}
        <footer className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-white/10 pb-2 pt-3 sm:flex-row sm:pb-3" data-testid="selector-footer">
          <img src="/tfs-connect/total-technologies.png" alt="Total Technologies — A Total Facility Solutions, Inc. division"
            className="h-[44px] w-auto shrink-0 object-contain opacity-100 [filter:contrast(1.08)_brightness(1.05)] sm:h-[51px]" data-testid="footer-total-technologies" />
          <FooterStatus />
        </footer>
      </div>
    </div>
  );
}

// ---- Portal card (horizontal, matches the app cards for alignment) ----
const PortalCard = ({ w, onActivate }) => {
  const [hot, setHot] = useState(false);
  const accent = accentOf(w);
  const active = isActive(w);
  const m = META[w.key] || { title: w.title, desc: w.subtitle };
  return (
    <button
      data-testid={`workspace-tile-${w.key}`}
      onClick={() => onActivate(w)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      aria-disabled={!active}
      style={{ borderColor: hot ? accent : 'rgba(255,255,255,0.10)', boxShadow: hot ? `0 0 0 3px ${hexA(accent, 0.28)}` : 'none' }}
      className="group relative flex h-full w-full min-w-0 items-center gap-3 overflow-hidden rounded-2xl border bg-white/[0.045] px-4 py-3 text-left outline-none backdrop-blur-sm transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08] focus-visible:-translate-y-0.5 active:scale-[0.98] active:translate-y-0 min-h-[86px]"
    >
      <CardIcon w={w} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-extrabold leading-tight text-white">{m.title}</div>
        <div className="mt-0.5 text-xs leading-snug text-slate-300">{m.desc}</div>
        <StatusRow w={w} />
      </div>
    </button>
  );
};

export default WorkspaceSelector;
